import { Clock, Facebook, MapPin, Phone } from "lucide-react";
import { Seo } from "../components/Seo";
import { Eyebrow, Reveal } from "../components/ui-bits";
import { HOURS, SITE } from "../data/site";

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact & Order | JIF Flowers, 1117 Main St Coventry RI"
        description="Order flowers by phone or in person: JIF – Jack Iannotti Flowers, 1117 Main Street, Coventry, RI. Call (401) 822-4440. Hours and directions."
      />
      <section className="mx-auto max-w-7xl px-5 pb-20 pt-40 sm:px-8">
        <Reveal>
          <Eyebrow>Contact & Order</Eyebrow>
          <h1 className="mt-5 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-plum sm:text-5xl lg:text-6xl">
            Call, or Come Say Hello
          </h1>
          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-charcoal/85 sm:text-[18px]">
            We take orders by phone or in person at the shop — no online forms, no checkout carts. Just a real
            conversation with a florist who will get the details right.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="flex h-full flex-col gap-6">
              <div className="rounded-xl border border-gold/40 bg-sagetint p-8">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-plum">
                    <Phone className="h-5 w-5 text-ivory" aria-hidden="true" />
                  </span>
                  <h2 className="font-serif text-2xl font-medium text-plum">Call to Order</h2>
                </div>
                <a
                  href={SITE.phoneHref}
                  data-testid="contact-phone-number"
                  className="mt-5 block font-serif text-4xl font-semibold tracking-tight text-plum transition-colors hover:text-berry sm:text-5xl"
                >
                  {SITE.phone}
                </a>
                <p className="mt-3 text-[15px] leading-relaxed text-charcoal/75">
                  For same-day delivery in most cases, call before 3:00 p.m.
                </p>
              </div>

              <div className="rounded-xl border border-gold/40 bg-ivory p-8 shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sage">
                    <MapPin className="h-5 w-5 text-ivory" aria-hidden="true" />
                  </span>
                  <h2 className="font-serif text-2xl font-medium text-plum">Visit the Shop</h2>
                </div>
                <address className="mt-5 text-[17px] not-italic leading-relaxed text-charcoal/90">
                  {SITE.name}
                  <br />
                  {SITE.address}
                </address>
                <a
                  href={SITE.directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions-button"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-plum hover:text-berry"
                >
                  Get Directions <span aria-hidden="true">→</span>
                </a>
              </div>

              <div className="rounded-xl border border-gold/40 bg-ivory p-8 shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gold">
                    <Clock className="h-5 w-5 text-ivory" aria-hidden="true" />
                  </span>
                  <h2 className="font-serif text-2xl font-medium text-plum">Shop Hours</h2>
                </div>
                <ul className="mt-5 space-y-2 text-[16px] text-charcoal/90">
                  {HOURS.map((h) => (
                    <li key={h.days} className="flex justify-between gap-6 border-b border-gold/20 pb-2">
                      <span className="font-bold">{h.days}</span>
                      <span>{h.time}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={SITE.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-facebook-link"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-plum transition-colors hover:text-plum"
                >
                  <Facebook className="h-4 w-4" aria-hidden="true" />
                  Find us on Facebook
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gold/40 shadow-xl shadow-sage/10">
              <iframe
                title="Map to JIF – Jack Iannotti Flowers, 1117 Main Street, Coventry, RI"
                src="https://www.google.com/maps?q=1117+Main+Street,+Coventry,+RI+02816&output=embed"
                className="h-[420px] w-full flex-1 lg:h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
              <div className="bg-plum p-8 text-ivory">
                <h2 className="font-serif text-2xl font-medium">Walk-ins always welcome</h2>
                <p className="mt-3 text-[15px] leading-relaxed text-ivory/85">
                  The walk-in cooler is stocked daily with ready-to-go arrangements, roses, and mixed bouquets. Stop
                  in and pick something out — or call ahead and we'll have it wrapped and waiting.
                </p>
                <div className="mt-6">
                  <a
                    href={SITE.phoneHref}
                    data-testid="contact-call-button"
                    className="inline-flex items-center gap-2.5 rounded-full bg-ivory px-7 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-plum transition-colors duration-300 hover:bg-white"
                  >
                    <Phone className="h-4 w-4" strokeWidth={2.2} aria-hidden="true" />
                    Call to Order: {SITE.phone}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
